import { forwardRef } from "react";

import { useTheme } from "@mui/material";

import { mergeSx } from "@/common/helpers/mergeSx";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormIcon, FormIconProps } from "@/forms-design-system/FormIcon/FormIcon";
import { FormTooltip, FormTooltipProps } from "@/forms-design-system/FormTooltip/FormTooltip";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { Info } from "@/icons/outlined";

type FormInfoTooltipProps = Pick<FormTooltipProps, "title" | "components" | "placement"> &
  Omit<FormIconProps, "children" | "title">;

export const FormInfoTooltip = forwardRef<HTMLDivElement, FormInfoTooltipProps>(
  ({ title, placement = "top-end", components, size = "small", sx, ...rest }, ref) => {
    const theme = useTheme();
    const { t } = useTranslate();

    if (!theme.palette.forms || !theme.palette.formsComponents?.tooltip) {
      return <InvalidThemeErrorMessage />;
    }

    const iconStyles = mergeSx(
      {
        display: "inline-block",
        color: theme.palette.formsComponents?.tooltip.iconColor,
        cursor: "help",
      },
      sx
    );

    return (
      <FormTooltip title={title} components={components} placement={placement} describeChild>
        <FormIcon
          ref={ref}
          size={size}
          sx={iconStyles}
          role="button"
          tabIndex={0}
          aria-label={t("common", "a11y.moreInformation")}
          {...rest}
        >
          <Info />
        </FormIcon>
      </FormTooltip>
    );
  }
);

FormInfoTooltip.displayName = "FormInfoTooltip";
